import {
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconAlertCircle, IconShieldCheck } from "@tabler/icons-react";
import { useEffect, useState, type FormEvent } from "react";

import { ApiError } from "@/api/client";
import { useCreateEvDcModbusTarget, useUpdateEvDcModbusTarget } from "@/api/queries";
import {
  EV_DC_MODBUS_PROFILES,
  type EvDcModbusProfile,
  type EvDcModbusTarget,
} from "@/api/types";
import { SiteSelect } from "@/components/SiteSelect";

type NumericValue = number | "";

type FormValues = {
  site_slug: string;
  device_slug: string;
  name: string;
  host: string;
  port: NumericValue;
  unit_id: NumericValue;
  profile: EvDcModbusProfile;
  external_id_prefix: string;
  address_offset: NumericValue;
  timeout_seconds: NumericValue;
  dlm_safe_current_a: NumericValue;
  dlm_comm_timeout_s: NumericValue;
  verify_readback_delay_ms: NumericValue;
  verify_retries: NumericValue;
  verify_tolerance: NumericValue;
};

type FieldErrors = Partial<Record<keyof FormValues, string>>;

export type EvDcModbusTargetFormModalProps = {
  opened: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  target?: EvDcModbusTarget;
  onSuccess?: (target: EvDcModbusTarget) => void;
};

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9_-]*[a-z0-9])?$/u;
const SLUG_HINT = "lowercase slug, [a-z0-9_-], must start and end with alphanumeric";
const UINT16_MAX = 65_535;

const PROFILE_OPTIONS: { value: EvDcModbusProfile; label: string }[] = [
  { value: "nidec", label: "Nidec" },
  { value: "cjnoo-gun1", label: "CJNOO gun 1" },
  { value: "cjnoo-gun2", label: "CJNOO gun 2" },
];

function defaultValues(
  mode: EvDcModbusTargetFormModalProps["mode"],
  target: EvDcModbusTarget | undefined,
): FormValues {
  if (mode === "edit" && target) {
    return {
      site_slug: target.site_slug,
      device_slug: target.device_slug,
      name: target.name,
      host: target.host,
      port: target.port,
      unit_id: target.unit_id,
      profile: target.profile,
      external_id_prefix: target.external_id_prefix,
      address_offset: target.address_offset,
      timeout_seconds: target.timeout_seconds,
      dlm_safe_current_a: target.dlm_safe_current_a,
      dlm_comm_timeout_s: target.dlm_comm_timeout_s,
      verify_readback_delay_ms: target.verify_readback_delay_ms,
      verify_retries: target.verify_retries,
      verify_tolerance: target.verify_tolerance,
    };
  }
  return {
    site_slug: "",
    device_slug: "",
    name: "",
    host: "",
    port: 502,
    unit_id: 1,
    profile: "nidec",
    external_id_prefix: "",
    address_offset: 0,
    timeout_seconds: 5,
    dlm_safe_current_a: 100,
    dlm_comm_timeout_s: 30,
    verify_readback_delay_ms: 0,
    verify_retries: 0,
    verify_tolerance: 0,
  };
}

function validate(values: FormValues, mode: "create" | "edit"): FieldErrors {
  const errors: FieldErrors = {};
  if (mode === "create") {
    if (values.site_slug.length === 0) {
      errors.site_slug = "required";
    } else if (!SLUG_PATTERN.test(values.site_slug)) {
      errors.site_slug = SLUG_HINT;
    }
    if (!SLUG_PATTERN.test(values.device_slug)) errors.device_slug = SLUG_HINT;
  }
  if (values.name.trim().length === 0) errors.name = "required";
  if (values.host.trim().length === 0) errors.host = "required";
  if (mode === "edit" && values.external_id_prefix.trim().length === 0) {
    errors.external_id_prefix = "required";
  }
  requireInt(errors, values, "port", 1, UINT16_MAX);
  requireInt(errors, values, "unit_id", 0, 255);
  requireInt(errors, values, "address_offset", -UINT16_MAX, UINT16_MAX);
  requirePositive(errors, values, "timeout_seconds");
  requireInt(errors, values, "dlm_safe_current_a", 1, UINT16_MAX);
  requireInt(errors, values, "dlm_comm_timeout_s", 1, UINT16_MAX);
  requireInt(errors, values, "verify_readback_delay_ms", 0, Number.MAX_SAFE_INTEGER);
  requireInt(errors, values, "verify_retries", 0, Number.MAX_SAFE_INTEGER);
  requireInt(errors, values, "verify_tolerance", 0, Number.MAX_SAFE_INTEGER);
  return errors;
}

export function EvDcModbusTargetFormModal({
  opened,
  onClose,
  mode,
  target,
  onSuccess,
}: EvDcModbusTargetFormModalProps) {
  const createMutation = useCreateEvDcModbusTarget();
  const updateMutation = useUpdateEvDcModbusTarget();
  const mutation = mode === "create" ? createMutation : updateMutation;

  const [values, setValues] = useState<FormValues>(() => defaultValues(mode, target));
  const [errors, setErrors] = useState<FieldErrors>({});

  useEffect(() => {
    if (opened) {
      setValues(defaultValues(mode, target));
      setErrors({});
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, mode, target?.id]);

  const set = <K extends keyof FormValues>(field: K, value: FormValues[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const fieldErrors = validate(values, mode);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    const common = {
      name: values.name.trim(),
      host: values.host.trim(),
      port: asNumber(values.port),
      unit_id: asNumber(values.unit_id),
      profile: values.profile,
      external_id_prefix:
        values.external_id_prefix.trim() === "" ? null : values.external_id_prefix.trim(),
      address_offset: asNumber(values.address_offset),
      timeout_seconds: asNumber(values.timeout_seconds),
      dlm_safe_current_a: asNumber(values.dlm_safe_current_a),
      dlm_comm_timeout_s: asNumber(values.dlm_comm_timeout_s),
      verify_readback_delay_ms: asNumber(values.verify_readback_delay_ms),
      verify_retries: asNumber(values.verify_retries),
      verify_tolerance: asNumber(values.verify_tolerance),
    };

    try {
      if (mode === "create") {
        const created = await createMutation.mutateAsync({
          site_slug: values.site_slug,
          device_slug: values.device_slug,
          writes_enabled: false,
          ...common,
        });
        onSuccess?.(created);
      } else if (target) {
        const updated = await updateMutation.mutateAsync({
          id: target.id,
          body: {
            ...common,
            external_id_prefix: common.external_id_prefix ?? target.external_id_prefix,
          },
        });
        onSuccess?.(updated);
      }
      onClose();
    } catch {
      // Error is surfaced via `mutation.error` and rendered inline below.
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={mode === "create" ? "Add EV DC Modbus target" : `Edit ${target?.name ?? "target"}`}
      centered
      size="lg"
    >
      <form onSubmit={handleSubmit} noValidate>
        <Stack>
          {mutation.error && <FormErrorAlert error={mutation.error} />}
          <Alert
            icon={<IconShieldCheck size={18} />}
            color="green"
            variant="light"
            title="Control writes are not changed here"
          >
            New targets are saved with control off. Existing control status is shown for review,
            but this form does not enable live writes.
          </Alert>

          {mode === "create" ? (
            <Stack gap="xs">
              <Text fw={600}>EV charger</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <SiteSelect
                  label="Site"
                  required
                  value={values.site_slug || null}
                  onChange={(v) => set("site_slug", v ?? "")}
                  error={errors.site_slug}
                />
                <TextInput
                  label="EV charger ID"
                  description="Use the device ID already registered under this site."
                  required
                  value={values.device_slug}
                  onChange={(e) => set("device_slug", e.currentTarget.value)}
                  error={errors.device_slug}
                />
              </SimpleGrid>
            </Stack>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <TextInput
                label="Site / EV charger"
                value={`${target?.site_slug ?? ""} / ${target?.device_slug ?? ""}`}
                readOnly
                variant="filled"
              />
              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  Control
                </Text>
                <Badge
                  variant="light"
                  color={target?.writes_enabled ? "orange" : "gray"}
                  size="lg"
                >
                  {target?.writes_enabled ? "On" : "Off"}
                </Badge>
              </Stack>
            </SimpleGrid>
          )}

          <Divider label="Connection" labelPosition="left" />
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Target name"
              required
              value={values.name}
              onChange={(e) => set("name", e.currentTarget.value)}
              error={errors.name}
            />
            <TextInput
              label="Adapter IP or host"
              description="Network address supplied by onsite commissioning."
              required
              value={values.host}
              onChange={(e) => set("host", e.currentTarget.value)}
              error={errors.host}
            />
            <NumberInput
              label="Network port"
              min={1}
              max={UINT16_MAX}
              value={values.port}
              onChange={(value) => set("port", numeric(value))}
              error={errors.port}
            />
            <NumberInput
              label="Modbus unit"
              min={0}
              max={255}
              value={values.unit_id}
              onChange={(value) => set("unit_id", numeric(value))}
              error={errors.unit_id}
            />
            <Select
              label="Connector profile"
              data={PROFILE_OPTIONS}
              allowDeselect={false}
              value={values.profile}
              onChange={(value) => {
                if (value && (EV_DC_MODBUS_PROFILES as readonly string[]).includes(value)) {
                  set("profile", value as EvDcModbusProfile);
                }
              }}
            />
            <TextInput
              label="Point ID prefix"
              description="Leave empty when adding a target to reuse the target name."
              value={values.external_id_prefix}
              onChange={(e) => set("external_id_prefix", e.currentTarget.value)}
              error={errors.external_id_prefix}
            />
            <NumberInput
              label="Register address adjustment"
              description="Use 0 unless the adapter map requires an offset."
              min={-UINT16_MAX}
              max={UINT16_MAX}
              value={values.address_offset}
              onChange={(value) => set("address_offset", numeric(value))}
              error={errors.address_offset}
            />
            <NumberInput
              label="Connection timeout"
              min={0.1}
              decimalScale={3}
              value={values.timeout_seconds}
              onChange={(value) => set("timeout_seconds", numeric(value))}
              error={errors.timeout_seconds}
            />
          </SimpleGrid>

          <Divider label="Safety and command check" labelPosition="left" />
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <NumberInput
              label="Fallback current limit"
              description="Amp limit to use when communication is not healthy."
              min={1}
              max={UINT16_MAX}
              value={values.dlm_safe_current_a}
              onChange={(value) => set("dlm_safe_current_a", numeric(value))}
              error={errors.dlm_safe_current_a}
            />
            <NumberInput
              label="Fallback after"
              description="Seconds without communication before fallback applies."
              min={1}
              max={UINT16_MAX}
              value={values.dlm_comm_timeout_s}
              onChange={(value) => set("dlm_comm_timeout_s", numeric(value))}
              error={errors.dlm_comm_timeout_s}
            />
            <NumberInput
              label="Delay before command check"
              description="Milliseconds to wait before checking the charger response."
              min={0}
              value={values.verify_readback_delay_ms}
              onChange={(value) => set("verify_readback_delay_ms", numeric(value))}
              error={errors.verify_readback_delay_ms}
            />
            <NumberInput
              label="Command check retries"
              min={0}
              value={values.verify_retries}
              onChange={(value) => set("verify_retries", numeric(value))}
              error={errors.verify_retries}
            />
            <NumberInput
              label="Allowed current difference"
              description="Amp difference allowed during read-back."
              min={0}
              value={values.verify_tolerance}
              onChange={(value) => set("verify_tolerance", numeric(value))}
              error={errors.verify_tolerance}
            />
          </SimpleGrid>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {mode === "create" ? "Create" : "Save changes"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function numeric(value: string | number): NumericValue {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : "";
  }
  return "";
}

function asNumber(value: NumericValue): number {
  return value === "" ? 0 : value;
}

function requireInt(
  errors: FieldErrors,
  values: FormValues,
  field: keyof FormValues,
  min: number,
  max: number,
) {
  const value = values[field];
  if (typeof value !== "number" || !Number.isInteger(value) || value < min || value > max) {
    errors[field] = `${min}..${max}`;
  }
}

function requirePositive(errors: FieldErrors, values: FormValues, field: keyof FormValues) {
  const value = values[field];
  if (typeof value !== "number" || value <= 0) {
    errors[field] = "must be greater than 0";
  }
}

function FormErrorAlert({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.code === "ev_dc_modbus_write_policy") {
    return (
      <Alert
        icon={<IconAlertCircle size={18} />}
        color="orange"
        variant="light"
        title="Write policy rejected"
      >
        CJNOO profiles are read-only for DLM writes.
      </Alert>
    );
  }
  if (error instanceof ApiError && error.code === "ev_dc_modbus_target_conflict") {
    return (
      <Alert
        icon={<IconAlertCircle size={18} />}
        color="orange"
        variant="light"
        title="Target already exists"
      >
        The target name or site/device pair is already registered.
      </Alert>
    );
  }
  if (error instanceof ApiError && error.code === "invalid_ev_dc_modbus_target") {
    return (
      <Alert
        icon={<IconAlertCircle size={18} />}
        color="orange"
        variant="light"
        title="Invalid device"
      >
        The selected device must be an active EV charger.
      </Alert>
    );
  }
  const message =
    error instanceof ApiError
      ? `${error.code}: ${error.message}`
      : error instanceof Error
        ? error.message
        : "Unknown error";
  return (
    <Alert icon={<IconAlertCircle size={18} />} color="red" variant="light" title="Save failed">
      {message}
    </Alert>
  );
}
